if (!customElements.get('ss-gallery-thumbnails')) {
  customElements.define('ss-gallery-thumbnails', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('product-gallery-navigation');
      this.previous = this.querySelector('[data-ss-thumb-prev]');
      this.next = this.querySelector('[data-ss-thumb-next]');
      if (!this.track) return;
      const update = () => {
        const items = [...this.track.querySelectorAll('button:not([hidden])')];
        const bounds = this.track.getBoundingClientRect();
        const first = items[0]?.getBoundingClientRect();
        const last = items.at(-1)?.getBoundingClientRect();
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.previous.disabled = !first || (rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2);
        this.next.disabled = !last || (rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2);
      };
      const move = direction => {
        const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.track.scrollBy({left: direction * rtl * this.track.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      this.previous.addEventListener('click', () => move(-1), {signal});
      this.next.addEventListener('click', () => move(1), {signal});
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; update(); });
      };
      this.track.addEventListener('scroll', schedule, {signal, passive: true});
      this.resize = new ResizeObserver(schedule);
      this.resize.observe(this.track);
      this.mutations = new MutationObserver(schedule);
      this.mutations.observe(this.track, {subtree: true, attributes: true, attributeFilter: ['hidden']});
      requestAnimationFrame(update);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); this.mutations?.disconnect(); cancelAnimationFrame(this.frame);
    }
  });
}
if (!customElements.get('ss-product-description')) {
  customElements.define('ss-product-description', class extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        const introTitle = this.dataset.compactIntro;
        if (introTitle && !this.querySelector('[data-ss-intro]')) {
          const firstParagraph = this.querySelector(':scope > p');
          const extra = [];
          let node = firstParagraph?.nextElementSibling;
          while (node && !node.matches('details, accordion-disclosure, h1, h2, h3, h4, h5, h6')) {
            extra.push(node);
            node = node.nextElementSibling;
          }
          if (extra.length) {
            const details = document.createElement('details');
            details.dataset.ssIntro = '';
            const summary = document.createElement('summary');
            summary.textContent = introTitle;
            firstParagraph.after(details);
            details.append(summary, ...extra);
          }
        }
        this.querySelectorAll('details:not(.accordion__disclosure)').forEach(details => {
          const summary = details.querySelector(':scope > summary');
          if (!summary) return;
          const label = document.createElement('span');
          label.textContent = summary.textContent.trim();
          summary.replaceChildren();
          const toggle = document.createElement('span');
          toggle.className = 'accordion__toggle';
          const icon = document.createElement('span');
          icon.className = 'animated-plus group-expanded:rotate';
          icon.setAttribute('aria-hidden', 'true');
          toggle.append(label, icon); summary.append(toggle);
          const content = document.createElement('div');
          content.className = 'accordion__content prose';
          [...details.childNodes].filter(node => node !== summary).forEach(node => {
            if (node.nodeType === 1 && node.classList.contains('ss-accordion__content')) {
              while (node.firstChild) content.append(node.firstChild);
              node.remove();
            } else content.append(node);
          });
          details.append(content);
          details.className = 'accordion__disclosure group';
          details.removeAttribute('style');
          details.setAttribute('aria-expanded', String(details.open));
          const wrapper = document.createElement('accordion-disclosure');
          wrapper.className = 'accordion';
          const parent = details.parentNode;
          const next = details.nextSibling;
          details.remove(); wrapper.append(details); parent.insertBefore(wrapper, next);
        });
      });
    }
  });
}

if (!customElements.get('ss-installation-choice')) {
  customElements.define('ss-installation-choice', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const details = this.querySelector('details');
      const output = this.querySelector('[data-ss-installation-summary]');
      const group = this.querySelector('.ss-installation-content');
      const error = this.querySelector('[data-ss-installation-error]');
      const root = this.closest('.shopify-section');
      const initialSummary = output.textContent;
      const requiredMessage = error.textContent;
      const selectedChoice = () => group.querySelector('[data-option-name="montage-service"] input[type="radio"]:checked');
      const clearError = () => {
        error.hidden = true;
        delete this.dataset.invalid;
        group.removeAttribute('aria-invalid');
      };
      const validate = event => {
        if (selectedChoice()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const input = group.querySelector('[data-option-name="montage-service"] input[type="radio"]');
        error.textContent = input ? requiredMessage : this.dataset.unavailableMessage;
        this.dataset.invalid = 'true';
        error.hidden = false;
        group.setAttribute('aria-invalid', 'true');
        details.open = true;
        this.scrollIntoView({block: 'center', behavior: 'instant'});
        (input || details.querySelector('summary')).focus({preventScroll: true});
      };
      root.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (button?.matches('[data-ss-action-cart]') || button?.matches('button[type="submit"]') && button.closest('.product-info [data-block-type="buy-buttons"]')) validate(event);
      }, {signal: this.controller.signal, capture: true});
      root.addEventListener('submit', event => {
        if (event.target.id === this.dataset.formId) validate(event);
      }, {signal: this.controller.signal, capture: true});
      const update = () => {
        const selected = selectedChoice();
        if (!selected) {
          delete this.dataset.selected;
          if (output.textContent !== initialSummary) output.textContent = initialSummary;
          return;
        }
        const label = selected && this.querySelector(`label[for="${CSS.escape(selected.id)}"]`);
        if (!label) return;
        const text = label.textContent.replace(/\s+/g, ' ').trim();
        if (output.textContent !== text) output.textContent = text;
        this.dataset.selected = 'true';
        clearError();
      };
      this.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"]')) return;
        update();
        if (this.dataset.selected) details.open = false;
      }, {signal: this.controller.signal});
      this.observer = new MutationObserver(update);
      this.observer.observe(this.querySelector('.ss-installation-content'), {childList: true, subtree: true});
      update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('ss-option-dropdown')) {
  customElements.define('ss-option-dropdown', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.querySelector('select').addEventListener('change', event => {
        const input = this.querySelector(`input[type="radio"][value="${CSS.escape(event.target.value)}"]`);
        if (input && !input.checked) input.click();
      }, {signal: this.controller.signal});
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
}

if (!customElements.get('ss-product-slider')) {
  customElements.define('ss-product-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const track = this.querySelector('.ss-product-slider__track');
      const previous = this.querySelector('[data-ss-products-prev]');
      const next = this.querySelector('[data-ss-products-next]');
      const update = () => {
        const bounds = track.getBoundingClientRect();
        const first = track.firstElementChild?.getBoundingClientRect();
        const last = track.lastElementChild?.getBoundingClientRect();
        const rtl = getComputedStyle(this).direction === 'rtl';
        previous.disabled = !first || (rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2);
        next.disabled = !last || (rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2);
        this.querySelector('.ss-product-slider__controls').hidden = previous.disabled && next.disabled;
      };
      const move = direction => {
        const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        track.scrollBy({left: direction * rtl * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1), {signal});
      next.addEventListener('click', () => move(1), {signal});
      track.addEventListener('scroll', update, {signal, passive: true});
      this.resize = new ResizeObserver(update);
      this.resize.observe(track);
      requestAnimationFrame(update);
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.resize?.disconnect(); }
  });
}

if (!customElements.get('ss-product-actions')) {
  customElements.define('ss-product-actions', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const root = this.closest('.shopify-section');
      const cart = this.querySelector('[data-ss-action-cart]');
      const quote = this.querySelector('[data-ss-action-quote]');
      const sourceButtons = () => root.querySelector('.product-info [data-block-type="buy-buttons"]');
      const cartTarget = () => sourceButtons()?.querySelector('buy-buttons button[type="submit"]');
      const quoteTarget = () => sourceButtons()?.querySelector('.omgrfq-btn-quotes');
      cart.addEventListener('click', () => cartTarget()?.click(), {signal});
      quote.addEventListener('click', () => quoteTarget()?.click(), {signal});
      root.addEventListener('invalid', event => {
        const details = event.target.closest('ss-installation-choice')?.querySelector('details');
        if (details) details.open = true;
      }, {signal, capture: true});
      const update = () => {
        const buyButtons = sourceButtons()?.querySelector('buy-buttons');
        const quoteWrapper = buyButtons?.querySelector(':scope > .omgrfq-button');
        if (quoteWrapper && buyButtons.parentElement) buyButtons.after(quoteWrapper);
        const original = cartTarget();
        const variantData = root.querySelector('variant-picker [data-variant]');
        if (variantData && this.variantJson !== variantData.textContent) {
          this.variantJson = variantData.textContent;
          try {
            const variant = JSON.parse(this.variantJson);
            const mediaId = variant?.featured_media?.id;
            if (mediaId && this.selectedVariant !== variant.id) {
              this.selectedVariant = variant.id;
              this.selectedMedia = mediaId;
              const thumbnail = root.querySelector(`product-gallery-navigation button[data-media-id="${mediaId}"]`);
              if (thumbnail && !thumbnail.hidden) thumbnail.click();
            }
          } catch {}
        }
        const cartDisabled = !original || original.disabled;
        if (cart.disabled !== cartDisabled) cart.disabled = cartDisabled;
        if (original && cart.textContent !== original.textContent) cart.textContent = original.textContent;
        const originalQuote = quoteTarget();
        const quoteDisabled = !originalQuote || originalQuote.disabled;
        if (quote.disabled !== quoteDisabled) quote.disabled = quoteDisabled;
        const price = root.querySelector('.product-info [data-block-type="price"] price-list');
        const output = this.querySelector('[data-ss-live-price]');
        if (price && this.priceHtml !== price.outerHTML) {
          this.priceHtml = price.outerHTML;
          const clone = price.cloneNode(true);
          clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          clone.removeAttribute('id');
          output.replaceChildren(clone);
        }
        const installation = root.querySelector('ss-installation-choice[data-selected] [data-ss-installation-summary]')?.textContent || '';
        const installationOutput = this.querySelector('[data-ss-live-installation]');
        if (installationOutput.textContent !== installation) installationOutput.textContent = installation;
        const buttons = sourceButtons();
        if (buttons && this.observedButtons !== buttons) {
          this.intersection?.disconnect();
          this.observedButtons = buttons;
          this.intersection = new IntersectionObserver(entries => {
            this.hidden = entries[0].isIntersecting;
          }, {threshold: .8});
          this.intersection.observe(buttons);
        }
        const preview = document.getElementById('PBarNextFrame');
        const offset = preview ? Math.min(80, preview.getBoundingClientRect().height) : 0;
        if (this.previewOffset !== offset) {
          this.previewOffset = offset;
          this.style.setProperty('--ss-preview-offset', `${offset}px`);
        }
      };
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; update(); });
      };
      this.observer = new MutationObserver(records => {
        if (records.some(record => {
          const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          const relevant = '[data-block-type="buy-buttons"], [data-block-type="price"], variant-picker, ss-installation-choice';
          if (target?.closest(relevant)) return true;
          return record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(relevant) || node.querySelector(relevant)));
        })) schedule();
      });
      const info = root.querySelector('.product-info');
      if (info) this.observer.observe(info, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'data-selected']});
      root.addEventListener('variant:change', schedule, {signal});
      window.addEventListener('resize', schedule, {signal, passive: true});
      update();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); this.intersection?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}



